import { faFolder } from '@fortawesome/pro-solid-svg-icons/faFolder';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	Badge,
	Colour,
	ColourOutline,
	ItemRow,
	ItemRowCell,
	ItemRowCheckbox,
	ItemRowSize,
	Pill,
	StackVertical,
	useLayout,
} from '@ukpostbox/frontend-framework';
import { CustomCheckbox } from '@ukpostbox/frontend-framework/lib/Components/Forms/Toggle/ToggleSwitchCustom';
import { FormatLocaleDateTime } from '@ukpostbox/helpers';
import React, { memo } from 'react';
import { useMailboxesStore } from '../../../Providers/Stores/MailboxesStore';
import { MailItemThumbnailsStacked } from '../../Thumbnails/MailItemThumbnailsStacked';
import { MailItemThumbnails } from '../../Thumbnails/MailItemThumbnails';
import { MailTagCollection } from '../../Tags/MailTagCollection';
import { MailItemMinimalComponentType } from '../../Types/MailItemTypes';

const ListViewMailItemMinimal = memo((props: MailItemMinimalComponentType) => {
	const { isTiny, isSmall, isMedium, isLarge } = useLayout();

	const mailBoxInfo = useMailboxesStore((state) => state.mailBoxInfo);

	const TopUpRequired = props.files[0]?.topupRequired || props.files[1]?.topupRequired;

	return (
		<ItemRow
			title={`Mail Item Minimal Entry ${props.mailUuid}`}
			muted={props.dateRead !== null}
			highlighted={!props.dateRead}
			selected={props.selected}
			static={!props.callBack}
			size={ItemRowSize.Large}
			extraProps={{
				'data-item-id': props.mailUuid,
				onClick: () => props.callBack && props.callBack(props.mailUuid),
			}}
			extraClass={props.forwardingPreview ? 'mail-item-forward-preview' : ''}
			disabled={props.disabled}
		>
			<ItemRowCheckbox>
				{props.selectable && (
					<CustomCheckbox
						onChange={(e) => null}
						label={props.mailUuid}
						name={`mailItem${props.mailUuid}`}
						hideLabel
						defaultChecked={props.selected}
					/>
				)}
			</ItemRowCheckbox>

			<div className='mailbox-item-meta mailbox-minimal' draggable={false}>
				{isTiny ? (
					<MailItemThumbnailsStacked files={props.files} />
				) : (
					<MailItemThumbnails files={props.files} />
				)}

				<ItemRowCell extraClass='mail-item-reference'>
					{!props.dateRead && <Badge colour={Colour.Primary} />}
					<StackVertical>
						<span className='mail-item-tracking-id'>{props.trackingId}</span>
						{props.clientFolder?.parentFolder && mailBoxInfo.customFolderId !== props.clientFolder?.id && (
							<div
								className={`pill flex-gap-xs ${props.clientFolder?.colour ? `${props.clientFolder?.colour}-solid` : Colour.Secondary}`}
							>
								<FontAwesomeIcon icon={faFolder} />
								{props.clientFolder?.description}
							</div>
						)}
						{props.daysUntilNextStorageCharge && (
							<Pill content='Storage Charge in 14 days' colour={ColourOutline.Warning} />
						)}
						{TopUpRequired && <Pill content='Top-up Required' colour={ColourOutline.Alert} />}
						{!props.daysUntilNextStorageCharge && !TopUpRequired && !props.dateRead && (
							<small>{FormatLocaleDateTime(new Date(props.dateReceived))}</small>
						)}
					</StackVertical>
				</ItemRowCell>

				{(!isTiny || !isSmall || !isMedium) && (
					<ItemRowCell extraClass='mail-item-tags item-row-tags'>
						{props.tags && (
							<MailTagCollection mailUuid={props.mailUuid} tags={props.tags} small displayLimit={3} />
						)}
					</ItemRowCell>
				)}

				{(!isTiny || !isSmall || !isMedium || !isLarge) && (
					<ItemRowCell extraClass='mail-item-notes'>
						<div className='notes-content-wrapper'>{props.note}</div>
					</ItemRowCell>
				)}
			</div>
		</ItemRow>
	);
});

export default ListViewMailItemMinimal;
